import { camelCaseToTitleCase } from '~/components/utils/formatters';
import blockedEmailDomains from '~/domains/common/constants/blockedEmailDomains.const';

// eslint-disable-next-line no-restricted-syntax
enum ErrorMessagePaths {
  REQUIRED = 'common.fieldIsRequired',
  INVALID = 'common.fieldIsInvalid',
  RESTRICTED = 'signUp.email.requirements.restricted',
  NOT_ALLOWED = 'signUp.handle.requirements.notAllowedChars',
  START_END_SPECIAL_CHARS = 'signUp.handle.requirements.startsEndsSpecialChars',
  CONSECUTIVE_SPECIAL_CHARS = 'signUp.handle.requirements.hasConsecutiveSpecialChars',
  TOO_SHORT = 'signUp.handle.requirements.tooShort',
  TOO_LONG = 'signUp.handle.requirements.tooLong',
  WHITE_SPACES = 'signUp.handle.requirements.containsWhiteSpaces',
  IS_EMAIL = 'signUp.handle.requirements.isEmailAddress',
}

// eslint-disable-next-line no-restricted-syntax
enum PasswordRequirements {
  PASSOWRD_LENGTH = 'signUp.password.requirements.minLength',
  PASSWORD_NUMBER = 'signUp.password.requirements.number',
  PASSWORD_LOWERCASE = 'signUp.password.requirements.lowercase',
  PASSWORD_UPPERCASE = 'signUp.password.requirements.uppercase',
  PASSWORD_SPECIAL_CHAR = 'signUp.password.requirements.specialChar',
}

export const specialCharactersRegex = /[!"#$%&'()*+,-./:;<=>?@[\]^_`{|}~]/;

const validateGenericField = (field, value): { label: ErrorMessagePaths; field: string } => {
  if (!value) {
    return { label: ErrorMessagePaths.REQUIRED, field: camelCaseToTitleCase(field) };
  }

  const isOnlySpaces = value.trim() === '';
  if (isOnlySpaces) {
    return { label: ErrorMessagePaths.INVALID, field: camelCaseToTitleCase(field) };
  }
};

const validateEmail = (field, value): { label: ErrorMessagePaths; field?: string } => {
  const genericError = validateGenericField(field, value);
  if (genericError) {
    return genericError;
  }

  const govEmailRegex = /(^@gov\.|\.gov\.|\.gov$)/i;
  const emailDomain = value.substring(value.indexOf('@'), value.length);

  if (
    !emailDomain ||
    /^[\w]{1,}[\w.+-]{0,}@[\w-]{2,}([.][a-zA-Z]{2,}|[.][\w-]{2,}[.][a-zA-Z]{2,})$/.test(value) ===
      false
  ) {
    return { label: ErrorMessagePaths.INVALID, field: 'Email' };
  }

  if (blockedEmailDomains.includes(emailDomain.toLowerCase()) || govEmailRegex.test(emailDomain)) {
    return { label: ErrorMessagePaths.RESTRICTED };
  }
};

const validateUsername = (field, value): { label: ErrorMessagePaths; field?: string } => {
  const genericError = validateGenericField(field, value);
  if (genericError) {
    return genericError;
  }

  const onlyAllowedChars = /^[a-zA-Z0-9._-]+$/.test(value);
  const startsWithAlphanumeric = /^[a-zA-Z0-9]/.test(value);
  const endsWithAlphanumeric = /[a-zA-Z0-9]$/.test(value);
  const hasConsecutiveSpecialChars = /(?=.*[._-]{2})/.test(value);

  const hasWhitespaces = /\s/.test(value);
  const isTooShort = value.length < 3;
  const isTooLong = value.length > 20;

  if (!onlyAllowedChars) {
    return { label: ErrorMessagePaths.NOT_ALLOWED };
  }
  if (!startsWithAlphanumeric || !endsWithAlphanumeric) {
    return { label: ErrorMessagePaths.START_END_SPECIAL_CHARS };
  }
  if (hasConsecutiveSpecialChars) {
    return { label: ErrorMessagePaths.CONSECUTIVE_SPECIAL_CHARS };
  }
  if (isTooShort) {
    return { label: ErrorMessagePaths.TOO_SHORT };
  }
  if (isTooLong) {
    return { label: ErrorMessagePaths.TOO_LONG };
  }
  if (hasWhitespaces) {
    return { label: ErrorMessagePaths.WHITE_SPACES };
  }
  if (value.includes('@')) {
    return { label: ErrorMessagePaths.IS_EMAIL };
  }
};

const validatePassword = (field, value, skipPasswordValidation = false) => {
  const genericError = validateGenericField(field, value);
  if (genericError) {
    return genericError;
  }

  if (skipPasswordValidation) {
    return;
  }

  const isTooShort = value.length < 8;
  const isTooLong = value.length > 256;
  const includesNumber = /[0-9]/.test(value);
  const includesLowercase = /[a-z]/.test(value);
  const includesUppercase = /[A-Z]/.test(value);
  const includesSpecialChars = specialCharactersRegex.test(value);
  if (
    !includesNumber ||
    !includesLowercase ||
    !includesUppercase ||
    !includesSpecialChars ||
    isTooShort ||
    isTooLong
  ) {
    return { label: ErrorMessagePaths.INVALID, field: 'Password' };
  }
};

export const validateField = (
  field,
  value,
  skipPasswordValidation = false
): {
  label: ErrorMessagePaths;
  field?: string;
} => {
  switch (field) {
    case 'username':
      return validateUsername(field, value);
    case 'email':
      return validateEmail(field, value);
    case 'password':
      return validatePassword(field, value, skipPasswordValidation);
    default:
      return validateGenericField(field, value);
  }
};

export const passwordRequirements = [
  { re: /^.{8,}$/, label: PasswordRequirements.PASSOWRD_LENGTH },
  { re: /[0-9]/, label: PasswordRequirements.PASSWORD_NUMBER },
  { re: /[a-z]/, label: PasswordRequirements.PASSWORD_LOWERCASE },
  { re: /[A-Z]/, label: PasswordRequirements.PASSWORD_UPPERCASE },
  {
    re: specialCharactersRegex,
    label: PasswordRequirements.PASSWORD_SPECIAL_CHAR,
  },
];

const getPasswordStrength = (password: string) => {
  let multiplier = password?.length > 5 ? 0 : 1;

  passwordRequirements.forEach((requirement) => {
    if (!requirement.re.test(password)) {
      multiplier += 1;
    }
  });

  return Math.max(100 - (100 / (passwordRequirements.length + 1)) * multiplier, 10);
};

export const getPasswordStrengthAndColor = (password: string) => {
  const strength = getPasswordStrength(password);
  const color = strength > 50 ? 'yellow.6' : 'red.6';
  const strengthColor = strength === 100 ? 'teal.4' : color;

  return { strengthColor, strength };
};
